import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useLogInMutation } from '../../queries/auth';
import AuthShell from '../../components/auth/AuthShell';
import { Banner, ErrorBanner, Field, PasswordField, Head, Submit, focusFirstInvalid } from '../../components/auth/parts';
import { classify } from '../../components/auth/authErrors';
import { emailOk } from '../../components/auth/validators';
import { isVerifiedValue } from '../../utils/authSession';
import { OPENING_FLAG } from '../../components/auth/OpeningDashboard';
import useCountdown from '../../components/auth/useCountdown';
import useAuthTitle from '../../components/auth/useAuthTitle';

const REASONS = { session_expired: 'expired', password_changed: 'resetdone' };

const Login = () => {
  const { t } = useTranslation();
  useAuthTitle('login');
  const a = (k, o) => t(`authPage.${k}`, o);
  const pair = (k) => t(`authPage.login.${k}`, { returnObjects: true });

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errs, setErrs] = useState({});
  // expired | resetdone (from ?reason=) | wrong | rate | offline | server
  const [banner, setBanner] = useState(REASONS[params.get('reason')] || null);
  const formRef = useRef(null);
  const cd = useCountdown();
  const logIn = useLogInMutation({ inline: true });

  // The lock lifts itself when the countdown ends.
  useEffect(() => {
    if (banner === 'rate' && cd.left === 0) setBanner(null);
  }, [banner, cd.left]);

  const locked = banner === 'rate' && cd.left > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (locked || logIn.isLoading) return;
    const next = {};
    if (!email.trim()) next.email = a('eReq');
    else if (!emailOk(email)) next.email = a('eEmail');
    if (!password) next.password = a('eReq');
    setErrs(next);
    if (Object.keys(next).length) { focusFirstInvalid(formRef.current); return; }

    setBanner(null);
    try {
      const data = await logIn.mutateAsync({ email: email.trim(), password });
      // An unverified account is sent to /verify/email by the hook instead.
      if (isVerifiedValue(data?.email_verified_at)) {
        // No artificial wait: navigate now. The "opening your dashboard…" view is shown by the
        // router only while the dashboard's data is really loading (see CompanyDetailsRouter).
        sessionStorage.setItem(OPENING_FLAG, '1');
        navigate('/dashboard');
      }
    } catch (err) {
      const info = classify(err);
      // Correct password, email not verified yet: the server issued no session. Go to the
      // verify-inbox view (it has the resend button), exactly like the old unverified path.
      if (info.status === 403 && info.code === 'email_not_verified') {
        localStorage.setItem('email', email.trim());
        localStorage.setItem('verification_attempts', Number.isFinite(parseInt(err?.response?.data?.verification_attempts, 10)) ? parseInt(err.response.data.verification_attempts, 10) : 0);
        localStorage.removeItem('email_verified_at');
        navigate('/verify/email');
        return;
      }
      if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); }
      else if (info.kind === 'offline') setBanner('offline');
      else if (info.kind === 'unauthorized' || info.kind === 'validation') setBanner('wrong'); // 401, or 422 (e.g. a <8-char password the API rejects before checking)
      else setBanner('server');
    }
  };

  return (
    <AuthShell>
      <Head title={a('login.h')} text={a('login.p')} />
      <form ref={formRef} noValidate onSubmit={onSubmit}>
        {banner === 'wrong' && <Banner kind="crit" icon="alert" pair={pair('wrong')} />}
        {banner === 'expired' && <Banner kind="info" icon="info" pair={pair('expired')} />}
        {banner === 'resetdone' && <Banner kind="good" icon="check" pair={pair('resetdone')} />}
        {(banner === 'rate' || banner === 'offline' || banner === 'server') && <ErrorBanner kind={banner} left={cd.left} />}

        <Field
          id="email" label={a('email')} type="email" ltr autoComplete="email" placeholder="name@cafe.sa"
          value={email} error={errs.email}
          onChange={(e) => { setEmail(e.target.value); setErrs((x) => ({ ...x, email: '' })); }}
        />
        <PasswordField
          id="password" label={a('pw')} autoComplete="current-password"
          value={password} error={errs.password}
          onChange={(e) => { setPassword(e.target.value); setErrs((x) => ({ ...x, password: '' })); }}
          aside={<Link to="/forget/password">{a('login.forgot')}</Link>}
        />
        <Submit busy={logIn.isLoading} busyLabel={a('login.busy')} disabled={locked}>{a('login.btn')}</Submit>
      </form>
      <p className="ppa-alt">{a('login.alt')} <Link to="/signup">{a('login.altA')}</Link></p>
    </AuthShell>
  );
};

export default Login;
